import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import {
  Box,
  makeStyles,
  Chip,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
} from "@material-ui/core";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import {
  updateExtendEndTimeBooking,
  fetchBookingDetail,
} from "../../store/slices/booking";
import AppDialog from "../Dialog/AppDialog";
import AppColor from "../../utilities/AppColor";
import moment from "moment";
import { EXTEND_TIME_STATUS } from "config/AppConfig";

const useStyles = makeStyles((theme) => ({
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
  },
  sliderContainer: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
    padding: theme.spacing(0, 2),
    display: "flex",
    justifyContent: "center",
  },
  durationDisplay: {
    marginTop: theme.spacing(2),
    textAlign: "center",
    fontSize: "1.2rem",
    color: theme.palette.primary.main,
  },
  markLabel: {
    transform: "translateX(-15%)",
  },
  contentText: {
    textAlign: "center",
    marginBottom: theme.spacing(2),
  },
  extendedTitleInfo: {
    textAlign: "left",
    marginTop: theme.spacing(2),
    fontWeight: 700,
    color: "#000",
  },
  extendedInfo: {
    textAlign: "left",
    marginTop: theme.spacing(2),
    marginLeft: theme.spacing(1),
    fontWeight: 500,
  },
}));

const AppExtendTimeDialog = ({ open, bookingId, onClose }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [showTimeSelection, setShowTimeSelection] = useState(false);
  const [bookingDetail, setBookingDetail] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [hour, setHour] = useState(1);
  const [minute, setMinute] = useState(30);
  const [timeType, setTimeType] = useState("minute");

  const TimeType = [
    {
      key: "hour",
      values: "hour",
      label: "Hour",
    },
    {
      key: "minute",
      values: "minute",
      label: "Minute",
    },
  ];

  const HourType = [
    {
      key: "1",
      values: "1",
      label: "1",
    },
    {
      key: "2",
      values: "2",
      label: "2",
    },
  ];

  const MinuteType = [
    {
      key: "0",
      values: "0",
      label: "0",
    },
    {
      key: "5",
      values: "5",
      label: "5",
    },
    {
      key: "10",
      values: "10",
      label: "10",
    },
    {
      key: "15",
      values: "15",
      label: "15",
    },
    {
      key: "20",
      values: "20",
      label: "20",
    },
    {
      key: "25",
      values: "25",
      label: "25",
    },
    {
      key: "30",
      values: "30",
      label: "30",
    },
    {
      key: "35",
      values: "35",
      label: "35",
    },
    {
      key: "40",
      values: "40",
      label: "40",
    },
    {
      key: "45",
      values: "45",
      label: "45",
    },
    {
      key: "50",
      values: "50",
      label: "50",
    },
    {
      key: "55",
      values: "55",
      label: "55",
    },
  ];

  const handleReject = async () => {
    try {
      await dispatch(
        updateExtendEndTimeBooking({
          bookingId,
          extendTimeStatus: "REJECTED",
        })
      );
      // Refresh booking detail after rejection
      const { payload } = await dispatch(fetchBookingDetail({ id: bookingId }));
      setBookingDetail(payload);
    } catch (error) {
      console.error("Error rejecting booking extension:", error);
    }
  };

  const handleApprove = async () => {
    if (!showTimeSelection) {
      setShowTimeSelection(true);
      return;
    }

    setIsProcessing(true);

    try {
      console.log(bookingDetail?.endTime);

      const endTime = moment(bookingDetail?.endTime);
      console.log("Original end time:", endTime.format("YYYY/MM/DD HH:mm"));
      const duration = timeType === "hour" ? (Number(hour * 60) + Number(minute)) : minute;
      console.log("Duration:", duration);
      endTime.add(duration, "minutes");
      const endTimeStr = endTime.format("YYYY/MM/DD HH:mm");
      console.log("New end time:", endTimeStr);

      await dispatch(updateExtendEndTimeBooking({
          bookingId,
          extendTimeStatus: 'APPROVED',
          endTimeStr
      }));

    //   Refresh booking detail after approval
      const { payload } = await dispatch(fetchBookingDetail({ id: bookingId }));
      setBookingDetail(payload);
    } catch (error) {
      console.error("Error approving booking extension:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    setShowTimeSelection(false);
    setHour(1);
    setMinute(30);
    setTimeType("minute");
    onClose();
  };

  useEffect(() => {
    const fetchData = async () => {
      if (open && bookingId) {
        try {
          const { payload } = await dispatch(
            fetchBookingDetail({ id: bookingId })
          );
          setBookingDetail(payload);
          console.log("Booking detail:", payload);
        } catch (error) {
          console.error("Failed to load booking detail:", error);
        }
      }
    };

    fetchData();
  }, [open, bookingId, dispatch]);

  const renderContent = () => {
    const extendTimeStatus = bookingDetail?.extendTimeStatus;
    console.log("extend", extendTimeStatus);
    if (extendTimeStatus === "REJECTED") {
      return (
        <>
          <p
            className={classes.extendedTitleInfo}
            style={{ textAlign: "center", fontSize: 18 }}
          >
            {t("title.shiftDetails")}
          </p>
          <div style={{ display: "flex", alignItems: "center" }}>
            <span className={classes.extendedTitleInfo}>
              {t("label.jobTitle")}:
            </span>
            <span className={classes.extendedInfo}>
              {bookingDetail?.job?.title}
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center" }}>
            <span className={classes.extendedTitleInfo}>
              {t("label.address")}:
            </span>
            <span className={classes.extendedInfo}>
              {bookingDetail?.job?.address1}
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center" }}>
            <span className={classes.extendedTitleInfo}>
              {t("label.date")}:
            </span>
            <span className={classes.extendedInfo}>
              {moment(bookingDetail?.job?.date).format("DD/MM/YYYY")}
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center" }}>
            <span className={classes.extendedTitleInfo}>
              {t("label.time")}:
            </span>
            <span className={classes.extendedInfo}>
              {`${t(`daysOfTheWeek.${bookingDetail?.dayOfWeek}`)}, ${t(
                `shift.${bookingDetail?.shiftType}`
              )} ${
                bookingDetail?.startTime
                  ? moment(bookingDetail?.startTime).format("HH:mm")
                  : "00:00"
              } - ${
                bookingDetail?.endTime
                  ? moment(bookingDetail?.endTime).format("HH:mm")
                  : "00:00"
              }`}
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center" }}>
            <span className={classes.extendedTitleInfo}>
              {t("label.status")}:
            </span>
            <span className={classes.extendedInfo}>
              <span className={classes.info}>
                <Chip
                  style={{
                    backgroundColor:
                      bookingDetail?.extendTimeStatus ===
                      EXTEND_TIME_STATUS.PENDING
                        ? AppColor.yellow
                        : bookingDetail?.extendTimeStatus ===
                          EXTEND_TIME_STATUS.APPROVED
                        ? AppColor.green
                        : bookingDetail?.extendTimeStatus ===
                          EXTEND_TIME_STATUS.REJECTED
                        ? AppColor.red
                        : AppColor.green,
                    color: AppColor.white,
                  }}
                  size="small"
                  label={t(
                    `label.extend_time_status.${bookingDetail?.extendTimeStatus}`
                  )}
                />
              </span>
            </span>
          </div>
        </>
      );
    }

    if (bookingDetail && extendTimeStatus === "APPROVED") {
      return (
        <>
          <p
            className={classes.extendedTitleInfo}
            style={{ textAlign: "center", fontSize: 18 }}
          >
            {t("title.shiftDetails")}
          </p>
          <div style={{ display: "flex", alignItems: "center" }}>
            <span className={classes.extendedTitleInfo}>
              {t("label.jobTitle")}:
            </span>
            <span className={classes.extendedInfo}>
              {bookingDetail?.job?.title}
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center" }}>
            <span className={classes.extendedTitleInfo}>
              {t("label.address")}:
            </span>
            <span className={classes.extendedInfo}>
              {bookingDetail?.job?.address1}
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center" }}>
            <span className={classes.extendedTitleInfo}>
              {t("label.date")}:
            </span>
            <span className={classes.extendedInfo}>
              {moment(bookingDetail?.job?.date).format("DD/MM/YYYY")}
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center" }}>
            <span className={classes.extendedTitleInfo}>
              {t("label.initial_time")}:
            </span>
            <span className={classes.extendedInfo}>
              {`${t(`daysOfTheWeek.${bookingDetail?.dayOfWeek}`)}, ${t(
                `shift.${bookingDetail?.shiftType}`
              )} ${
                bookingDetail?.startTime
                  ? moment(bookingDetail?.startTime).format("HH:mm")
                  : "00:00"
              } - ${
                bookingDetail?.previousEndTime
                  ? moment(bookingDetail?.previousEndTime).format("HH:mm")
                  : "00:00"
              }`}
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center" }}>
            <span className={classes.extendedTitleInfo}>
              {t("label.actual_time")}:
            </span>
            <span className={classes.extendedInfo}>
              {`${t(`daysOfTheWeek.${bookingDetail?.dayOfWeek}`)}, ${t(
                `shift.${bookingDetail?.shiftType}`
              )} ${
                bookingDetail?.startTime
                  ? moment(bookingDetail?.startTime).format("HH:mm")
                  : "00:00"
              } - ${
                bookingDetail?.endTime
                  ? moment(bookingDetail?.endTime).format("HH:mm")
                  : "00:00"
              }`}
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center" }}>
            <span className={classes.extendedTitleInfo}>
              {t("label.status")}:
            </span>
            <span className={classes.extendedInfo}>
              <span className={classes.info}>
                <Chip
                  style={{
                    backgroundColor:
                      bookingDetail?.extendTimeStatus ===
                      EXTEND_TIME_STATUS.PENDING
                        ? AppColor.yellow
                        : bookingDetail?.extendTimeStatus ===
                          EXTEND_TIME_STATUS.APPROVED
                        ? AppColor.green
                        : bookingDetail?.extendTimeStatus ===
                          EXTEND_TIME_STATUS.REJECTED
                        ? AppColor.red
                        : AppColor.green,
                    color: AppColor.white,
                  }}
                  size="small"
                  label={t(
                    `label.extend_time_status.${bookingDetail?.extendTimeStatus}`
                  )}
                />
              </span>
            </span>
          </div>
        </>
      );
    }

    return !showTimeSelection ? (
      <p className={classes.contentText}>
        <p
          className={classes.extendedTitleInfo}
          style={{ textAlign: "center", fontSize: 18 }}
        >
          {t("title.shiftDetails")}
        </p>
        {bookingDetail && bookingDetail?.extendTimeStatus === "PENDING" && (
          <>
            <div style={{ display: "flex", alignItems: "center" }}>
              <span className={classes.extendedTitleInfo}>
                {t("label.jobTitle")}:
              </span>
              <span className={classes.extendedInfo}>
                {bookingDetail?.job?.title}
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "center" }}>
              <span className={classes.extendedTitleInfo}>
                {t("label.address")}:
              </span>
              <span className={classes.extendedInfo}>
                {bookingDetail?.job?.address1}
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "center" }}>
              <span className={classes.extendedTitleInfo}>
                {t("label.date")}:
              </span>
              <span className={classes.extendedInfo}>
                {moment(bookingDetail?.job?.date).format("DD/MM/YYYY")}
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "center" }}>
              <span className={classes.extendedTitleInfo}>
                {t("label.time")}:
              </span>
              <span className={classes.extendedInfo}>
                {`${t(`daysOfTheWeek.${bookingDetail?.dayOfWeek}`)}, ${t(
                  `shift.${bookingDetail?.shiftType}`
                )} ${
                  bookingDetail?.startTime
                    ? moment(bookingDetail?.startTime).format("HH:mm")
                    : "00:00"
                } - ${
                  bookingDetail?.endTime
                    ? moment(bookingDetail?.endTime).format("HH:mm")
                    : "00:00"
                }`}
              </span>
            </div>
          </>
        )}
      </p>
    ) : (
      <>
        <Box className={classes.sliderContainer}>
          {/* <Slider
                        value={duration}
                        onChange={handleSliderChange}
                        aria-labelledby="duration-slider"
                        min={30}
                        max={120}
                        marks={marks}
                        valueLabelDisplay="auto"
                        valueLabelFormat={formatDuration}
                        classes={{
                            markLabel: classes.markLabel,
                        }}
                    />
                    <p className={classes.durationDisplay}>
                        {formatDuration(duration)}
                    </p> */}
          {timeType === "hour" && (
            <FormControl className={classes.formControl}>
              <InputLabel id="hour-type-label">Select Hour</InputLabel>
              <Select
                labelId="hour-type-label"
                id="hour-type-select"
                onChange={(e) => setHour(e.target.value)}
                value={hour}
              >
                {HourType.map((item) => {
                  return (
                    <MenuItem key={item.key} value={item.key}>
                      {item.label}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          )}
          <FormControl className={classes.formControl}>
            <InputLabel id="minute-type-label">Select Minute</InputLabel>
            <Select
              labelId="minute-type-label"
              id="minute-type-select"
              onChange={(e) => setMinute(e.target.value)}
              value={minute}
            >
              {MinuteType.map((item) => {
                return (
                  <MenuItem key={item.key} value={item.key}>
                    {item.label}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          <FormControl className={classes.formControl}>
            <InputLabel id="time-type-label">Select Time</InputLabel>
            <Select
              labelId="time-type-label"
              id="time-type-select"
              onChange={(e) => setTimeType(e.target.value)}
              value={timeType}
            >
              {TimeType.map((item) => {
                return (
                  <MenuItem key={item.key} value={item.key}>
                    {item.label}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Box>
      </>
    );
  };

  const getDialogActions = () => {
    const extendTimeStatus = bookingDetail?.extendTimeStatus;

    if (["REJECTED", "APPROVED"].includes(extendTimeStatus)) {
      return [];
    }

    return !showTimeSelection
      ? [
          {
            label: t("button.reject"),
            onPress: handleReject,
            buttonColor: AppColor.red,
            mode: "outlined",
          },
          {
            label: t("button.approve"),
            onPress: handleApprove,
            buttonColor: AppColor.mainColor,
          },
        ]
      : [
          {
            label: t("button.back"),
            onPress: () => setShowTimeSelection(false),
            buttonColor: AppColor.mainColor,
            mode: "text",
            disabled: isProcessing,
            loading: isProcessing,
          },
          {
            label: t("button.confirm"),
            onPress: handleApprove,
            buttonColor: AppColor.mainColor,
            disabled: isProcessing,
            loading: isProcessing,
          },
        ];
  };

  return (
    <AppDialog
      open={open}
      onClose={handleClose}
      title={t("label.extend_booking_duration")}
      maxWidth="sm"
      actions={getDialogActions()}
    >
      {renderContent()}
    </AppDialog>
  );
};

AppExtendTimeDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  bookingId: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};

export default AppExtendTimeDialog;
